import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import { MainLayout } from "@/app/layouts/MainLayout";
import { NuqsRouterAdapter } from "@/app/providers/NuqsRouterAdapter";
import { ErpLayout } from "@/app/layouts/ErpLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ProvisioningGuard } from "@/components/auth/ProvisioningGuard";
import { AppAccessGuard } from "@/components/auth/AppAccessGuard";
import { AppInstallGuard } from "@/components/auth/AppInstallGuard";
import { SupportOnlyGate } from "@/components/auth/SupportOnlyGate";
import { LoginPage } from "@/features/auth/LoginPage";
import { RegisterPage } from "@/features/auth/RegisterPage";
import { LineCallbackPage } from "@/features/auth/LineCallbackPage";

const ProvisioningPreview = lazy(() =>
  import("@/features/auth/components/registration/ProvisioningScreen").then(m => ({ default: m.ProvisioningScreenPreview }))
);
import { PhoneReVerifyPage } from "@/features/auth/PhoneReVerifyPage";
import { PrintPage } from "@/features/print/PrintPage";
import { PrintStudioPage } from "@/features/print/PrintStudioPage";
import { PrintLayout } from "@/app/layouts/PrintLayout";
import { PageEditorPage } from "@/features/storefront/page-editor";
import { erpLayoutChildren } from "./erpRoutes";
import { iframeAppRoutes } from "./iframeAppRoutes";

const AcceptInvitationPage = lazy(() => import("@/features/auth/AcceptInvitationPage"));
const JoinViaLinkPage = lazy(() => import("@/features/auth/JoinViaLinkPage"));
const POSGatePage = lazy(() => import("@/features/pos/POSGatePage").then(m => ({ default: m.POSGatePage })));
const DocumentCreatorPage = lazy(() => import("@/features/print/DocumentCreatorPage").then(m => ({ default: m.DocumentCreatorPage })));
const PickListPage = lazy(() => import("@/features/fulfillment/PickListPage").then(m => ({ default: m.PickListPage })));
const PickWalkPage = lazy(() => import("@/features/fulfillment/PickWalkPage").then(m => ({ default: m.PickWalkPage })));
const SortPage = lazy(() => import("@/features/fulfillment/SortPage").then(m => ({ default: m.SortPage })));
const PackStationPage = lazy(() => import("@/features/fulfillment/PackStationPage").then(m => ({ default: m.PackStationPage })));
const BatchCompletePage = lazy(() => import("@/features/fulfillment/BatchCompletePage").then(m => ({ default: m.BatchCompletePage })));
const CardDesignerPage = lazy(() => import("@/features/rewards/components/CardDesignerPage").then(m => ({ default: m.CardDesignerPage })));
const StorefrontAtlasPage = lazy(() => import("@/features/storefront/atlas").then(m => ({ default: m.StorefrontAtlasPage })));

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/register/provisioning-preview",
    element: <Suspense fallback={null}><ProvisioningPreview /></Suspense>,
  },
  {
    path: "/auth/line/callback",
    element: <LineCallbackPage />,
  },
  {
    path: "/auth/verify-phone",
    element: <PhoneReVerifyPage />,
  },
  // Accept invitation route (public, key-based auth)
  {
    path: "/invite/accept",
    element: (
      <Suspense fallback={null}>
        <AcceptInvitationPage />
      </Suspense>
    ),
  },
  // Join via shared invite link (public, code-based auth)
  {
    path: "/invite/join",
    element: (
      <Suspense fallback={null}>
        <JoinViaLinkPage />
      </Suspense>
    ),
  },
  // POS route OUTSIDE MainLayout (full-screen kiosk experience)
  {
    path: "/pos",
    element: (
      <ProtectedRoute>
        <NuqsRouterAdapter />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <AppAccessGuard requiredApp="orders">
            <ProvisioningGuard>
              <Suspense fallback={null}>
                <POSGatePage />
              </Suspense>
            </ProvisioningGuard>
          </AppAccessGuard>
        ),
      },
    ],
  },
  // Fulfillment immersive routes OUTSIDE MainLayout (no AppShell, no sidebar, no padding)
  {
    path: "/fulfillment/pick-list/:sessionId",
    element: (
      <ProtectedRoute>
        <AppAccessGuard requiredApp="products">
          <ProvisioningGuard>
            <Suspense fallback={null}>
              <PickListPage />
            </Suspense>
          </ProvisioningGuard>
        </AppAccessGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/fulfillment/pick/:sessionId",
    element: (
      <ProtectedRoute>
        <AppAccessGuard requiredApp="products">
          <ProvisioningGuard>
            <Suspense fallback={null}>
              <PickWalkPage />
            </Suspense>
          </ProvisioningGuard>
        </AppAccessGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/fulfillment/sort/:sessionId",
    element: (
      <ProtectedRoute>
        <AppAccessGuard requiredApp="products">
          <ProvisioningGuard>
            <Suspense fallback={null}>
              <SortPage />
            </Suspense>
          </ProvisioningGuard>
        </AppAccessGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/fulfillment/pack/:sessionId",
    element: (
      <ProtectedRoute>
        <AppAccessGuard requiredApp="products">
          <ProvisioningGuard>
            <Suspense fallback={null}>
              <PackStationPage />
            </Suspense>
          </ProvisioningGuard>
        </AppAccessGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/fulfillment/complete/:sessionId",
    element: (
      <ProtectedRoute>
        <AppAccessGuard requiredApp="products">
          <ProvisioningGuard>
            <Suspense fallback={null}>
              <BatchCompletePage />
            </Suspense>
          </ProvisioningGuard>
        </AppAccessGuard>
      </ProtectedRoute>
    ),
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <NuqsRouterAdapter />
      </ProtectedRoute>
    ),
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            // Support-only tenants are locked to /support (+ /settings for logout);
            // renders <Outlet/> unchanged for every normal tenant.
            element: <SupportOnlyGate />,
            children: [
              // ============================================
              // ERP ROUTES — wrapped in ErpLayout (persistent sidebar)
              // ============================================
              {
                element: <ErpLayout />,
                children: erpLayoutChildren,
              },

              // ============================================
              // NON-ERP ROUTES — directly under MainLayout (no sidebar)
              // Includes iframe apps (CRM, Raven, Insights, Inbox, Affine),
              // full-bleed editors, and utility pages.
              // ============================================
              ...iframeAppRoutes,
            ],
          },
        ],
      },
    ],
  },
  // Print route outside MainLayout (isolated print experience)
  {
    path: "/print/:doctype/:name",
    element: (
      <ProtectedRoute>
        <PrintLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <PrintPage />,
      },
    ],
  },
  // Card Designer — full-screen tier card design studio (OPS-1833)
  {
    path: "/rewards/card-designer",
    element: (
      <ProtectedRoute>
        <PrintLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <CardDesignerPage />
          </Suspense>
        ),
      },
    ],
  },
  // Page Customizer Editor — full-bleed, no sidebar (matches Print Studio layout)
  {
    path: "/storefront/page-editor/:pageId",
    element: (
      <ProtectedRoute>
        <PrintLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <AppInstallGuard requiredApp="frappe_builder">
            <AppAccessGuard requiredApp="builder">
              <PageEditorPage />
            </AppAccessGuard>
          </AppInstallGuard>
        ),
      },
    ],
  },
  // Storefront Atlas — full-bleed funnel graph + live iframe inspector (Plan 1945)
  {
    path: "/storefront/atlas",
    element: (
      <ProtectedRoute>
        <PrintLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <AppInstallGuard requiredApp="frappe_builder">
            <AppAccessGuard requiredApp="builder">
              <Suspense fallback={null}>
                <StorefrontAtlasPage />
              </Suspense>
            </AppAccessGuard>
          </AppInstallGuard>
        ),
      },
    ],
  },
  // Document Creator — standalone document creation app (like POS)
  // Must come BEFORE :doctype/:name to avoid "create" matching as a param
  {
    path: "/print-studio/create",
    element: (
      <ProtectedRoute>
        <PrintLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <DocumentCreatorPage />
          </Suspense>
        ),
      },
    ],
  },
  // Print Studio (v2) — read-only React print renderer for ERPNext documents
  {
    path: "/print-studio/:doctype/:name",
    element: (
      <ProtectedRoute>
        <PrintLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <PrintStudioPage />,
      },
    ],
  },
]);
